/**
 * pendientesGanador — acceso compartido a GET /sorteos/pendientes-ganador.
 *
 * PROBLEMA QUE RESUELVE
 * Al entrar a /banca/saldos se disparaban DOS solicitudes idénticas en
 * paralelo: una desde `SaldosPage` (banner de sorteos sin ganador) y otra
 * desde `AppLayout` (contador del menú Premios, que se recarga en cada
 * cambio de ruta). Ese endpoint agrupa el historial completo de tickets sin
 * ganador; ejecutarlo dos veces a la vez duplica el trabajo más caro de la
 * pantalla.
 *
 * SOLUCIÓN
 * Un único punto de acceso con:
 *   1. Coalescencia de solicitudes en vuelo: si ya hay un GET en curso, los
 *      llamadores concurrentes comparten esa misma promesa.
 *   2. Micro-caché muy corta, para cubrir el caso en que un llamador arranca
 *      unos milisegundos después de que el otro ya resolvió.
 *
 * NO es una caché de sesión: la ventana es de 2 segundos, así que al navegar
 * los datos se vuelven a pedir. Ambos consumidores siguen recibiendo su propia
 * copia del arreglo y manejan sus propios errores.
 *
 * Sin dependencias nuevas: solo `fetchAPI`, ya usado en todo el proyecto.
 */

import { fetchAPI } from './apiClient';

export interface SorteoPendienteGanador {
  id: string;
  nombre: string;
  tipo_id?: string;
  fecha: string;
  hora_cierre?: string | null;
  estado?: string;
}

const ENDPOINT = '/sorteos/pendientes-ganador';

/** Ventana de coalescencia. Suficiente para cubrir el montaje de una ruta. */
export const VENTANA_COALESCENCIA_MS = 2000;

let enVuelo: Promise<SorteoPendienteGanador[]> | null = null;
let ultimo: { datos: SorteoPendienteGanador[]; en: number } | null = null;

/**
 * Devuelve los sorteos pendientes de ganador.
 *
 * Garantiza UNA sola solicitud HTTP efectiva cuando varios componentes la
 * piden a la vez (por ejemplo, al entrar a Saldos).
 *
 * @param opciones.forzar Ignora la micro-caché y la solicitud en vuelo.
 */
export function obtenerPendientesGanador(
  opciones: { forzar?: boolean } = {},
): Promise<SorteoPendienteGanador[]> {
  if (opciones.forzar) {
    invalidarPendientesGanador();
  } else {
    if (ultimo && Date.now() - ultimo.en < VENTANA_COALESCENCIA_MS) {
      return Promise.resolve(ultimo.datos);
    }
    if (enVuelo) return enVuelo;
  }

  const solicitud = fetchAPI<SorteoPendienteGanador[]>(ENDPOINT)
    .then((datos) => {
      const lista = Array.isArray(datos) ? datos : [];
      ultimo = { datos: lista, en: Date.now() };
      return lista;
    })
    .finally(() => {
      if (enVuelo === solicitud) enVuelo = null;
    });

  enVuelo = solicitud;
  return solicitud;
}

/** Descarta la micro-caché (por ejemplo, tras asignar un ganador). */
export function invalidarPendientesGanador(): void {
  ultimo = null;
  enVuelo = null;
}

/** Solo para pruebas: deja el módulo en estado inicial. */
export function _resetPendientesGanadorParaTests(): void {
  invalidarPendientesGanador();
}
